<template>
	<div class="mini-table">
		<SimpleHScroll :bounce="false">
			<div class="mini-table__wrapper">
				<!-- 隐藏的插槽容器，用于收集列定义 -->
				<div class="hidden-columns">
					<slot></slot>
				</div>
				<!-- 表头 -->
				<div class="mini-table__header">
					<div class="mini-table__header-row">
						<div
							v-for="(column, columnIndex) in columns"
							:key="column.prop"
							:style="composeColumnStyle(column)"
							class="mini-table__header-cell"
						>
							<div class="cell">
								<!-- 如果有自定义表头渲染函数，使用自定义渲染 -->
								<component
									v-if="column.renderHeaderSlot"
									:is="column.renderHeaderSlot"
									:column="column"
									:columnIndex="columnIndex"
								/>
								<!-- 否则渲染默认表头 -->
								<span v-else>{{ column.label }}</span>
							</div>
						</div>
					</div>
				</div>
				<!-- 表体 -->
				<div class="mini-table__body">
					<template v-if="data && data.length > 0">
						<div v-for="(row, rowIndex) in data" :key="rowIndex" class="mini-table__row">
							<div
								v-for="column in columns"
								:key="column.prop"
								:style="composeColumnStyle(column)"
								class="mini-table__cell"
							>
								<div class="cell">
									<!-- 如果有自定义插槽内容，渲染插槽 -->
									<component
										v-if="column.renderSlot"
										:is="column.renderSlot"
										:row="row"
										:column="column"
										:rowIndex="rowIndex"
									/>
									<!-- 如果有 formatter 函数，使用 formatter 格式化 -->
									<span v-else-if="column.formatter">
										{{ column.formatter(row, column, getValueByProp(row, column.prop), rowIndex) }}
									</span>
									<!-- 否则渲染默认内容 -->
									<span v-else>{{ getValueByProp(row, column.prop) }}</span>
								</div>
							</div>
						</div>
					</template>
					<!-- 空状态 -->
					<div v-else class="mini-table__row">
						<div class="mini-table__cell">
							<div class="cell cell--empty">
								<span>{{ $t('enterprise.no-data') }}</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</SimpleHScroll>
	</div>
</template>

<script>
export default {
	name: 'MiniTable'
};
</script>
<script setup>
import { ref, provide } from 'vue';
import { SimpleHScroll } from '@rebuild/components/scroll';

const props = defineProps({
	data: {
		type: Array,
		default: () => []
	}
});
provide('tableData', () => props.data);

const columns = ref([]);
// 注册子列
const registerColumn = column => {
	// 检查是否已存在相同 prop 的列，如果存在则更新，否则添加
	const existingIndex = columns.value.findIndex(col => col.prop === column.prop);
	if (existingIndex > -1) {
		columns.value[existingIndex] = column;
	} else {
		columns.value.push(column);
	}
};
// 注销子列，应对 TableColumn 条件渲染的场景
const unregisterColumn = prop => {
	const index = columns.value.findIndex(col => col.prop === prop);
	if (index > -1) {
		columns.value.splice(index, 1);
	}
};
provide('registerColumn', registerColumn);
provide('unregisterColumn', unregisterColumn);

const composeColumnStyle = column => {
	let style = {};
	if (column.width) {
		// to rem
		style['width'] = `${column.width / 100}rem`;
		style['flex'] = 'none';
	}
	if (column.align) {
		style['text-align'] = column.align;
	}
	return style;
};

// 获取嵌套属性值
const defaultVal = '-';
const getValueByProp = (obj, prop) => {
	if (!prop || !obj) return defaultVal;

	// 支持嵌套属性，如 'user.name'
	const keys = prop.split('.');
	let value = obj;

	for (const key of keys) {
		if (value === null || value === undefined) {
			return defaultVal;
		}
		value = value[key];
	}

	return value === null || value === undefined ? defaultVal : value;
};
</script>

<style lang="less" scoped>
@import '../../../../style/mixins';

.mini-table {
	.thinner-border-around(#e8eaef, 0);
	&::before {
		border-top: none;
	}

	.hidden-columns {
		visibility: hidden;
		position: absolute;
		z-index: -1;
	}

	.cell {
		padding: 0.08rem;
		white-space: pre-wrap;

		color: #666;
		/* 12/B1-regular */
		font-family: 'PingFang SC';
		font-size: 0.12rem;
		font-style: normal;
		font-weight: 400;
		line-height: 140%; /* 16.8px */

		&--empty {
			text-align: center;
		}
	}

	&__header-row,
	&__row {
		display: flex;
	}

	&__header-cell,
	&__cell {
		flex: 1;

		&:not(:first-child) {
			text-align: right;
		}
	}

	&__header {
		background: var(--ir-theme-color-opacity-1);
		.thinner-border-around(var(--ir-theme-color-opacity-1), 0);
	}

	&__body {
		.mini-table__row + .mini-table__row {
			.thinner-border-top(#e8eaef);
		}

		.cell {
			color: #333;
		}
	}
}
</style>
